// frontend/src/pages/admin/AdminDashboardPage.js
import React, { useMemo } from 'react';
import useFetch from '../../hooks/useFetch';
import { 
    FaUsers, FaCreditCard, FaLayerGroup, FaMoneyBillWave, 
    FaUserSecret, FaEye, FaCalendarWeek, FaCalendarAlt, FaHistory 
} from 'react-icons/fa';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis 
} from 'recharts';

// --- RENK PALETİ ---
const COLORS = ['#4A69E2', '#FF6B6B', '#2ECC71', '#F1C40F', '#9B59B6', '#E84393'];

const tooltipStyle = {
    backgroundColor: '#1E2133',
    border: '1px solid #374151', 
    borderRadius: '8px',
    color: '#fff', 
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
    padding: '10px',
    outline: 'none'
};

const StatCard = ({ title, value, icon, color, subText }) => (
  <div className="bg-surface border border-border p-5 rounded-2xl shadow-lg relative overflow-hidden group hover:border-gray-500 transition-all duration-300">
    <div className="flex justify-between items-start z-10 relative">
      <div>
        <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-white">{value}</h3>
        {subText && <p className="text-[10px] text-gray-500 mt-1">{subText}</p>}
      </div>
      <div className={`p-3 rounded-xl ${color} bg-opacity-20 text-white text-lg shadow-inner`}>
        {icon}
      </div>
    </div>
    {/* Arkaplan Glow Efekti */}
    <div className={`absolute -bottom-6 -right-6 w-24 h-24 rounded-full blur-3xl opacity-20 ${color}`}></div>
  </div>
);

const AdminDashboardPage = () => {
  const { data: users, loading: usersLoading } = useFetch('/users');
  const { data: subs, loading: subsLoading } = useFetch('/subscriptions');
  const { data: payments, loading: payLoading } = useFetch('/payments');
  const { data: activityStats } = useFetch('/extras/admin-stats');

  const stats = useMemo(() => {
    if (!users || !subs || !payments) return null;

    const totalUsers = users.length;
    const activeApps = subs.filter(s => s.isActive).length;
    const monthlyCost = subs.filter(s => s.isActive).reduce((acc, curr) => acc + curr.price, 0);
    const yearlyCost = monthlyCost * 12;

    // 1. Uygulama Bazlı Maliyet (Pie & Radar Chart için)
    const appCostData = subs
        .filter(s => s.isActive)
        .map(s => ({ name: s.name, value: s.price, fullMark: 500 })) // fullMark radar için
        .sort((a, b) => b.value - a.value)
        .slice(0, 6); // İlk 6 uygulama

    // 2. Aylık Ödeme Projeksiyonu (DÜZELTİLMİŞ ALGORİTMA)
    const monthlyData = [];
    const today = new Date();
    
    // Gelecek 6 Ayı Döngüye Al
    for(let i=0; i<6; i++) {
        // Hedef Ay ve Yılı Belirle
        const targetDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
        const targetMonth = targetDate.getMonth();
        const targetYear = targetDate.getFullYear();
        
        // Etiketi oluştur (Örn: Oca 25)
        const labelName = targetDate.toLocaleDateString('tr-TR', { month: 'short', year: '2-digit' });
        
        // Sadece o AY ve o YIL'a ait ödemeleri topla
        const total = payments
            .filter(p => {
                const pDate = new Date(p.paymentDate);
                return pDate.getMonth() === targetMonth && pDate.getFullYear() === targetYear;
            })
            .reduce((acc, curr) => acc + curr.amount, 0);
        
        // Eğer o ay hiç ödeme yoksa (henüz planlanmadıysa), tahmini gideri yaz
        // (Planlanmışsa gerçek toplamı, planlanmamışsa sabit gideri göster)
        monthlyData.push({ 
            name: labelName, 
            Tutar: total > 0 ? total : monthlyCost,
            Tahmin: total === 0 // Renk ayrımı için flag
        });
    }

    return { totalUsers, activeApps, monthlyCost, yearlyCost, appCostData, monthlyData };
  }, [users, subs, payments]);

  if (usersLoading || subsLoading || payLoading) return <div className="text-white text-center p-10 animate-pulse">Veriler yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-10">
      <div className="flex justify-between items-end">
        <div>
            <h1 className="text-3xl font-bold text-white">Genel Bakış</h1>
            <p className="text-text-secondary text-sm">Sistem metrikleri ve finansal özet.</p>
        </div>
        <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-500 font-bold">SON GÜNCELLEME</p>
            <p className="text-sm text-primary font-mono">{new Date().toLocaleTimeString('tr-TR')}</p>
        </div>
      </div>

      {/* --- KARTLAR BÖLÜMÜ (4 + 4) --- */}
      
      {/* 1. SATIR: GENEL DURUM */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="TOPLAM ÜYE" value={stats?.totalUsers || 0} icon={<FaUsers />} color="bg-blue-600" />
        <StatCard title="AKTİF UYGULAMA" value={stats?.activeApps || 0} icon={<FaLayerGroup />} color="bg-purple-600" />
        <StatCard title="AYLIK GİDER" value={`₺${stats?.monthlyCost?.toFixed(0)}`} icon={<FaCreditCard />} color="bg-red-600" />
        <StatCard title="YILLIK PROJEKSİYON" value={`₺${stats?.yearlyCost?.toFixed(0)}`} icon={<FaMoneyBillWave />} color="bg-green-600" />
      </div>

      {/* 2. SATIR: ZİYARETÇİ VE AKTİVİTE */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
            title="ONLINE KULLANICI" 
            value={activityStats?.onlineUsers?.length || 0} 
            icon={<FaUserSecret />} 
            color="bg-teal-500 animate-pulse" 
            subText="Son 5 dakika"
        />
        <StatCard 
            title="BUGÜN ZİYARET" 
            value={activityStats?.summary?.visitsToday || 0} 
            icon={<FaEye />} 
            color="bg-indigo-500" 
            subText="Sayfa Görüntülenme"
        />
        <StatCard 
            title="BU HAFTA ZİYARET" 
            value={activityStats?.summary?.visitsWeek || 0} 
            icon={<FaCalendarWeek />} 
            color="bg-orange-500" 
        />
        <StatCard 
            title="BU AY ZİYARET" 
            value={activityStats?.summary?.visitsMonth || 0} 
            icon={<FaCalendarAlt />} 
            color="bg-pink-500" 
        />
      </div>

      {/* --- GRAFİKLER BÖLÜMÜ --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* SOL: AYLIK GRAFİK */}
          <div className="lg:col-span-2 bg-surface border border-border p-6 rounded-2xl shadow-lg">
              <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                  <FaCreditCard className="text-blue-500"/> 6 Aylık Ödeme Projeksiyonu
              </h3>
              <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats?.monthlyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                          <XAxis dataKey="name" stroke="#9CA3AF" tick={{fill: '#9CA3AF', fontSize: 12}} tickLine={false} axisLine={false} />
                          <YAxis stroke="#9CA3AF" tick={{fill: '#9CA3AF', fontSize: 12}} tickLine={false} axisLine={false} tickFormatter={(value) => `₺${value}`} />
                          <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#fff' }} cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }} formatter={(value) => `₺${value}`} />
                          <Bar dataKey="Tutar" radius={[4, 4, 0, 0]} barSize={50}>
                              {stats?.monthlyData.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.Tahmin ? '#6B7280' : '#4A69E2'} /> // Tahminse Gri, Gerçekse Mavi
                              ))}
                          </Bar>
                      </BarChart>
                  </ResponsiveContainer>
              </div>
              <div className="mt-4 flex gap-4 justify-center text-xs">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 bg-blue-600 rounded-sm"></span> <span className="text-gray-400">Planlanmış Ödeme</span></div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 bg-gray-500 rounded-sm"></span> <span className="text-gray-400">Sabit Gider Tahmini</span></div>
              </div>
          </div>

          {/* SAĞ: RADAR CHART (YENİ ÖZELLİK) */}
          <div className="bg-surface border border-border p-6 rounded-2xl shadow-lg flex flex-col items-center justify-center">
              <h3 className="text-lg font-bold text-white mb-2 w-full text-left flex items-center gap-2">
                  <FaLayerGroup className="text-purple-500"/> Maliyet Radarı
              </h3>
              <div className="h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                      <RadarChart cx="50%" cy="50%" outerRadius="70%" data={stats?.appCostData}>
                          <PolarGrid stroke="#374151" />
                          <PolarAngleAxis dataKey="name" tick={{ fill: '#9CA3AF', fontSize: 10 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 'auto']} tick={false} axisLine={false} />
                          <Radar name="Maliyet" dataKey="value" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                          <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: '#fff' }} formatter={(value) => `₺${value}`}/>
                      </RadarChart>
                  </ResponsiveContainer>
              </div>
              <p className="text-xs text-gray-500 text-center mt-2">Uygulamaların birbirine göre maliyet ağırlığı.</p>
          </div>
      </div>

      {/* --- ALT BÖLÜM: TABLO VE TİMELİNE --- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* SOL: SON ÖDEMELER TABLOSU */}
          <div className="lg:col-span-2 bg-surface border border-border p-6 rounded-2xl shadow-lg">
              <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-white">Son Planlanan Ödemeler</h3>
                  <button className="text-xs text-primary hover:underline">Tümünü Gör</button>
              </div>
              <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm text-text-secondary">
                      <thead className="bg-gray-800 text-white uppercase font-bold text-xs">
                          <tr>
                              <th className="p-3">Tarih</th>
                              <th className="p-3">Üye</th>
                              <th className="p-3">Tutar</th>
                              <th className="p-3 text-center">Durum</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                          {payments && payments.slice(0, 5).map(pay => (
                              <tr key={pay._id} className="hover:bg-gray-800/50 transition-colors">
                                  <td className="p-3 font-mono text-gray-300">{new Date(pay.paymentDate).toLocaleDateString('tr-TR')}</td>
                                  <td className="p-3 font-bold text-white flex items-center gap-2">
                                      <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] text-white">
                                          {pay.userId?.name?.charAt(0) || '?'}
                                      </div>
                                      {pay.userId?.name || 'Bilinmiyor'}
                                  </td>
                                  <td className="p-3 text-white font-bold">₺{pay.amount.toFixed(2)}</td>
                                  <td className="p-3 text-center">
                                      <span className={`px-2 py-1 rounded text-[10px] font-bold ${pay.status === 'completed' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`}>
                                          {pay.status === 'completed' ? 'ÖDENDİ' : 'BEKLİYOR'}
                                      </span>
                                  </td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
                  {(!payments || payments.length === 0) && <p className="text-center p-4 text-gray-500">Henüz işlem yok.</p>}
              </div>
          </div>

          {/* SAĞ: SON AKTİVİTELER (YENİ ÖZELLİK) */}
          <div className="bg-surface border border-border p-6 rounded-2xl shadow-lg flex flex-col">
               <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <FaHistory className="text-orange-500"/> Canlı Akış
              </h3>
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 max-h-[300px]">
                  <ul className="space-y-4 relative border-l border-gray-700 ml-2 pl-4">
                      {activityStats?.recentActivities?.map((act, idx) => (
                          <li key={act._id} className="relative group">
                              <span className={`absolute -left-[21px] top-1 w-3 h-3 rounded-full border-2 border-surface ${act.type.includes('copy') ? 'bg-red-500' : 'bg-blue-500'} group-hover:scale-125 transition-transform`}></span>
                              <div className="flex justify-between items-start">
                                  <div>
                                      <p className="text-xs text-gray-400 mb-0.5">{new Date(act.createdAt).toLocaleTimeString('tr-TR')}</p>
                                      <p className="text-sm font-bold text-white">{act.userId?.name || 'Silinmiş Üye'}</p>
                                      <p className="text-xs text-gray-500 mt-0.5 break-words">{act.details || act.type}</p>
                                  </div>
                              </div>
                          </li>
                      ))}
                      {(!activityStats?.recentActivities || activityStats.recentActivities.length === 0) && (
                          <li className="text-xs text-gray-500 italic">Henüz aktivite yok.</li>
                      )}
                  </ul>
              </div>
          </div>

      </div>
    </div>
  );
};

export default AdminDashboardPage;